import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { NotebookPen, Save, Loader2, SmilePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const MOODS = [
  { id: "great", emoji: "\u{1F60A}", label: "Страхотно", labelEn: "Great" },
  { id: "good", emoji: "\u{1F642}", label: "Добре", labelEn: "Good" },
  { id: "okay", emoji: "\u{1F610}", label: "Нормално", labelEn: "Okay" },
  { id: "bad", emoji: "\u{1F61E}", label: "Зле", labelEn: "Bad" },
];

const SYMPTOMS = [
  { id: "bloating", label: "Подуване", labelEn: "Bloating" },
  { id: "fatigue", label: "Умора", labelEn: "Fatigue" },
  { id: "headache", label: "Главоболие", labelEn: "Headache" },
  { id: "craving", label: "Глад за сладко", labelEn: "Sugar craving" },
  { id: "insomnia", label: "Безсъние", labelEn: "Insomnia" },
  { id: "anxiety", label: "Тревожност", labelEn: "Anxiety" },
  { id: "pain", label: "Болка", labelEn: "Pain" },
  { id: "nausea", label: "Гадене", labelEn: "Nausea" },
];

export default function DailyDiaryNote() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [note, setNote] = useState("");
  const [mood, setMood] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchNote = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/meals/day-note`);
      setNote(data.note || "");
      setMood(data.mood || null);
      setSymptoms(data.symptoms || []);
      if (data.note || data.mood || (data.symptoms && data.symptoms.length > 0)) {
        setExpanded(true);
      }
    } catch { /* silent */ }
    finally { setLoaded(true); }
  }, [API]);

  useEffect(() => { fetchNote(); }, [fetchNote]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/meals/day-note`, { note, mood, symptoms });
      toast.success(bg ? "Бележката е запазена" : "Note saved");
    } catch { toast.error(bg ? "Грешка" : "Error"); }
    finally { setSaving(false); }
  };

  const toggleSymptom = (id) => {
    setSymptoms(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  if (!loaded) return null;

  return (
    <div className={`rounded-xl border p-3 ${isDark ? 'bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]' : 'bg-white border-gray-100'}`}
      data-testid="daily-diary-note">
      <button onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left">
        <NotebookPen className={`w-3.5 h-3.5 ${isDark ? 'text-[#F59E0B]' : 'text-amber-500'}`} />
        <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          {bg ? "Дневни бележки" : "Daily notes"}
        </span>
        {mood && <span className="text-sm">{MOODS.find(m => m.id === mood)?.emoji}</span>}
        {symptoms.length > 0 && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-500'}`}>
            {symptoms.length} {bg ? "симпт." : "sympt."}
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-2.5 space-y-2.5">
          {/* Mood selector */}
          <div>
            <p className={`text-[10px] font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <SmilePlus className="w-3 h-3 inline mr-1" />
              {bg ? "Как се чувствате днес?" : "How do you feel today?"}
            </p>
            <div className="flex gap-1.5">
              {MOODS.map(m => (
                <button key={m.id} onClick={() => setMood(mood === m.id ? null : m.id)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] transition-all ${
                    mood === m.id
                      ? isDark ? 'bg-[#F59E0B]/15 text-[#F59E0B] ring-1 ring-[#F59E0B]/30' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                      : isDark ? 'bg-[hsl(223,47%,15%)] text-gray-400' : 'bg-gray-50 text-gray-500'
                  }`}
                  data-testid={`mood-${m.id}`}
                >
                  <span className="text-sm">{m.emoji}</span>
                  {bg ? m.label : m.labelEn}
                </button>
              ))}
            </div>
          </div>

          {/* Symptoms */}
          <div>
            <p className={`text-[10px] font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {bg ? "Симптоми (ако има):" : "Symptoms (if any):"}
            </p>
            <div className="flex flex-wrap gap-1">
              {SYMPTOMS.map(s => (
                <button key={s.id} onClick={() => toggleSymptom(s.id)}
                  className={`px-2 py-0.5 rounded-full text-[10px] transition-all ${
                    symptoms.includes(s.id)
                      ? isDark ? 'bg-red-900/20 text-red-400 ring-1 ring-red-400/30' : 'bg-red-50 text-red-600 ring-1 ring-red-200'
                      : isDark ? 'bg-[hsl(223,47%,15%)] text-gray-500' : 'bg-gray-50 text-gray-400'
                  }`}
                  data-testid={`symptom-${s.id}`}
                >
                  {bg ? s.label : s.labelEn}
                </button>
              ))}
            </div>
          </div>

          {/* Note text */}
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={bg ? "Бележки за деня: какво ядох, как се чувствам, лекарства, допълнителна информация..." : "Notes for the day: what I ate, how I feel, medications, additional info..."}
            className={`w-full h-14 p-2 text-xs rounded-lg border resize-none ${
              isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white placeholder:text-gray-600' : 'border-gray-200 placeholder:text-gray-400'
            }`}
            data-testid="diary-note-text"
          />

          {/* Save button */}
          <Button size="sm" variant="outline" className={`h-7 text-[10px] ${isDark ? 'border-[hsl(223,47%,20%)]' : ''}`}
            onClick={save} disabled={saving} data-testid="save-diary-note">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            {bg ? "Запазване" : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}
